import type { CreateClientInput, ServiceRelationshipConfig } from "@closer/shared";
import { CLIENT_DUPLICATE_CODE, createClientSchema } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * YENİ MÜVEKKİL — İKİ ADIM, İKİ EKSEN (CLOS-1122 / CLOS-1140, rapor 1 §3, §12).
 *
 * ### 1. adım: Temsili Müvekkil / Closer Müvekkil (kullanım biçimi)
 * Kullanıcı önce müvekkilin Closer'a davet edilip edilmeyeceğine karar verir. Uzun bir form
 * yerine iki kart; adlar raporun verdiği adlar, uydurma terim yok ("Gerçek Müvekkil" vb.).
 *
 * ### 2. adım: Gerçek kişi / Kurum (hukuki varlık türü) + type-aware form
 * Closer Müvekkilde gerçek kişi için davet MÜVEKKİLİN KENDİ e-postasına gider — ikinci bir
 * "temsilci" alanı yoktur (R1.2). Kurumda davet, kurum adına kabul edecek ilk yetkilinin
 * e-postasına gider; ilk yetkili adı yalnız iletişim kişisi olur, yetki üretmez (S4).
 *
 * ### ⚠ Hizmet kapsamı zorunlu ve VARSAYILANSIZ (Halit S3)
 * Bir hukuk ürününde önemli bir ilişkinin fark edilmeden varsayılan kapsamla açılması
 * istenmez; kullanıcı açıkça seçer.
 *
 * ### ⚠ "Müvekkili oluştur ve davet gönder" TEK istek
 * Kayıt + davet aynı sunucu işleminde (`closerInvite`); "müvekkil var, davet yok" yarım
 * durumu oluşmaz. Eski "Closer'da mevcut müvekkil / Yeni müvekkil" dalları (CLOS-1116) bu
 * modelle supersede edildi.
 */
type HataAlani =
  | "firstName"
  | "lastName"
  | "nationalId"
  | "legalName"
  | "taxNumber"
  | "taxOffice"
  | "email"
  | "phone";

function alanHatasi(errors: unknown, alan: HataAlani): string | undefined {
  const kayit = errors as Record<string, { message?: string } | undefined>;
  return kayit[alan]?.message;
}

function davetHatasi(
  errors: unknown,
  alan: "targetEmail" | "scope" | "contactName",
): string | undefined {
  const kayit = errors as { closerInvite?: Record<string, { message?: string } | undefined> };
  return kayit.closerInvite?.[alan]?.message;
}

export interface ClientCreateModalProps {
  open: boolean;
  onClose: () => void;
}

type Tur = CreateClientInput["clientType"];
/** Kullanım biçimi: Temsili (davet yok) / Closer (davetle). Kolon değil, akış. */
export type MusvekkilYolu = "representative" | "closer";

function bosForm(clientType: Tur, yol: MusvekkilYolu): CreateClientInput {
  const ortak = { email: "", phone: "", acknowledgeDuplicate: false };
  const davet =
    yol === "closer" ? { closerInvite: { targetEmail: "", scope: "", contactName: "" } } : {};
  return clientType === "individual"
    ? ({
        clientType,
        firstName: "",
        lastName: "",
        nationalId: "",
        ...ortak,
        ...davet,
      } as CreateClientInput)
    : ({
        clientType,
        legalName: "",
        taxNumber: "",
        taxOffice: "",
        ...ortak,
        ...davet,
      } as CreateClientInput);
}

export function ClientCreateModal({ open, onClose }: ClientCreateModalProps) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adaylar, setAdaylar] = useState<string[]>([]);
  const [yol, setYol] = useState<MusvekkilYolu | null>(null);

  const form = useZodForm(createClientSchema, {
    defaultValues: bosForm("individual", "representative"),
  });
  const tur = form.watch("clientType");

  /** Kapsam sözlüğü politikadan — yalnız Closer yolunda gerekir. */
  const politika = useQuery({
    enabled: open && yol === "closer",
    queryKey: tenantKey(tenantId, "service-policy"),
    queryFn: async () =>
      (await api<ServiceRelationshipConfig>("/api/clients/service-policy", { tenantId })).data,
  });

  const mutation = useMutation({
    mutationFn: async (values: CreateClientInput) =>
      (
        await api<{ id: string }>("/api/clients", {
          method: "POST",
          tenantId,
          body: JSON.stringify(values),
        })
      ).data,
    onSuccess: async (client) => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "clients") });
      toast.success(
        yol === "closer" ? "Müvekkil oluşturuldu ve davet gönderildi" : "Müvekkil oluşturuldu",
      );
      kapat();
      navigate(`/app/muvekkiller/${client.id}`);
    },
    onError: (error) => {
      const yinelenenler =
        error instanceof ApiRequestError
          ? (error.fields ?? []).filter((f) => f.code === CLIENT_DUPLICATE_CODE)
          : [];
      if (yinelenenler.length > 0) {
        setAdaylar(yinelenenler.map((f) => f.message));
        return;
      }
      applyServerErrors(form, error);
      toast.error(error instanceof ApiRequestError ? error.message : "Müvekkil oluşturulamadı");
    },
  });

  function kapat() {
    form.reset(bosForm("individual", "representative"));
    setAdaylar([]);
    setYol(null);
    onClose();
  }

  function yolSec(secilen: MusvekkilYolu) {
    form.reset(bosForm("individual", secilen));
    setAdaylar([]);
    setYol(secilen);
  }

  function turDegistir(yeni: Tur) {
    const { email, phone, closerInvite } = form.getValues();
    form.reset({
      ...bosForm(yeni, yol ?? "representative"),
      email,
      phone,
      ...(yol === "closer" ? { closerInvite } : {}),
    } as CreateClientInput);
    setAdaylar([]);
  }

  /** Gerçek kişide davet adresi = müvekkilin kendi e-postası; kayıt da o adresi taşır. */
  function govde(values: CreateClientInput, acknowledgeDuplicate: boolean): CreateClientInput {
    if (yol !== "closer") {
      const { closerInvite: _yok, ...kalan } = values;
      return { ...kalan, acknowledgeDuplicate } as CreateClientInput;
    }
    const davet = values.closerInvite!;
    const temiz = {
      ...davet,
      contactName: davet.contactName ? davet.contactName : undefined,
    };
    return {
      ...values,
      email: values.clientType === "individual" ? davet.targetEmail : values.email,
      closerInvite: temiz,
      acknowledgeDuplicate,
    } as CreateClientInput;
  }

  const gonder = form.handleSubmit((values) => {
    setAdaylar([]);
    mutation.mutate(govde(values, false));
  });
  const yineDeOlustur = form.handleSubmit((values) => mutation.mutate(govde(values, true)));

  const kapsamlar = politika.data?.scopeTypes ?? [];
  const closer = yol === "closer";

  return (
    <Modal
      open={open}
      onClose={kapat}
      title="Yeni Müvekkil"
      footer={
        yol === null ? (
          <Button variant="secondary" onClick={kapat}>
            Vazgeç
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                form.reset(bosForm("individual", "representative"));
                setAdaylar([]);
                setYol(null);
              }}
            >
              Geri
            </Button>
            <Button variant="secondary" onClick={kapat}>
              Vazgeç
            </Button>
            {adaylar.length > 0 ? (
              <Button variant="danger" onClick={yineDeOlustur} loading={mutation.isPending}>
                Yine de oluştur
              </Button>
            ) : (
              <Button type="submit" form="client-create-form" loading={mutation.isPending}>
                {closer ? "Müvekkili oluştur ve davet gönder" : "Oluştur"}
              </Button>
            )}
          </>
        )
      }
    >
      {yol === null ? (
        <div className="flex flex-col gap-3" data-testid="musvekkil-yolu">
          <div className="flex flex-col gap-1 rounded-md border border-border-default p-3">
            <span className="text-sm font-medium text-fg-strong">Temsili Müvekkil</span>
            <span className="text-sm text-fg-soft">
              Closer hesabı veya e-posta gerekmez. Müvekkilin sözleşmelerini yalnız hukuk büronuz
              içinde takip edin.
            </span>
            <div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => yolSec("representative")}
              >
                Temsili Müvekkil oluştur
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-1 rounded-md border border-border-default p-3">
            <span className="text-sm font-medium text-fg-strong">Closer Müvekkil</span>
            <span className="text-sm text-fg-soft">
              Müvekkili Closer&apos;a davet edin. Daveti kabul ettiğinde ortak bir Müvekkil Alanında
              çalışmaya başlayın.
            </span>
            <div>
              <Button type="button" size="sm" variant="secondary" onClick={() => yolSec("closer")}>
                Closer Müvekkil oluştur
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <form id="client-create-form" onSubmit={gonder} noValidate className="flex flex-col gap-4">
          <p className="text-xs text-fg-soft">
            {closer
              ? "Closer Müvekkil: davet kabul edilene kadar ilişki ve Müvekkil Alanı aktif olmaz."
              : "Temsili Müvekkil: davet gönderilmez, Müvekkil Alanı oluşmaz."}
          </p>
          <FormField label="Müvekkil türü" required>
            <Select
              value={tur}
              onChange={(e) => turDegistir(e.target.value as Tur)}
              aria-label="Müvekkil türü"
            >
              <option value="individual">Gerçek kişi</option>
              <option value="organization">Kurum</option>
            </Select>
          </FormField>

          {tur === "individual" ? (
            <>
              <FormField label="Ad" error={alanHatasi(form.formState.errors, "firstName")} required>
                <Input autoComplete="given-name" {...form.register("firstName")} />
              </FormField>
              <FormField
                label="Soyad"
                error={alanHatasi(form.formState.errors, "lastName")}
                required
              >
                <Input autoComplete="family-name" {...form.register("lastName")} />
              </FormField>
              {/* ⚠ Kimlik numarası ZORUNLU DEĞİL: kart veri minimizasyonu diyor. */}
              <FormField
                label="Kimlik numarası"
                error={alanHatasi(form.formState.errors, "nationalId")}
              >
                <Input inputMode="numeric" {...form.register("nationalId")} />
              </FormField>
            </>
          ) : (
            <>
              <FormField
                label="Ticari unvan"
                error={alanHatasi(form.formState.errors, "legalName")}
                required
              >
                <Input autoComplete="organization" {...form.register("legalName")} />
              </FormField>
              <FormField
                label="Vergi numarası"
                error={alanHatasi(form.formState.errors, "taxNumber")}
              >
                <Input inputMode="numeric" {...form.register("taxNumber")} />
              </FormField>
              <FormField
                label="Vergi dairesi"
                error={alanHatasi(form.formState.errors, "taxOffice")}
              >
                <Input {...form.register("taxOffice")} />
              </FormField>
            </>
          )}

          {closer ? (
            <>
              {tur === "organization" ? (
                <FormField
                  label="İlk yetkili adı"
                  error={davetHatasi(form.formState.errors, "contactName")}
                >
                  <Input autoComplete="off" {...form.register("closerInvite.contactName")} />
                </FormField>
              ) : null}
              {/* Gerçek kişide davet MÜVEKKİLİN KENDİ adresine; kurumda ilk yetkilinin. */}
              <FormField
                label={tur === "individual" ? "Müvekkilin e-postası" : "İlk yetkili e-postası"}
                error={davetHatasi(form.formState.errors, "targetEmail")}
                required
              >
                <Input
                  type="email"
                  autoComplete="email"
                  required
                  {...form.register("closerInvite.targetEmail")}
                />
              </FormField>
              <FormField
                label="Hizmet kapsamı"
                error={davetHatasi(form.formState.errors, "scope")}
                required
              >
                <Select
                  aria-label="Hizmet kapsamı"
                  required
                  {...form.register("closerInvite.scope")}
                >
                  <option value="">Hizmet kapsamı seçin</option>
                  {kapsamlar.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            </>
          ) : (
            <FormField label="E-posta" error={alanHatasi(form.formState.errors, "email")}>
              <Input type="email" autoComplete="email" {...form.register("email")} />
            </FormField>
          )}
          <FormField label="Telefon" error={alanHatasi(form.formState.errors, "phone")}>
            <Input autoComplete="tel" {...form.register("phone")} />
          </FormField>

          {adaylar.length > 0 ? (
            <div role="alert" className="flex flex-col gap-1 rounded-md bg-warning-subtle p-3">
              <p className="text-sm font-medium text-fg-strong">Olası yinelenen kayıt</p>
              {adaylar.map((metin) => (
                <p key={metin} className="text-sm text-fg-muted">
                  {metin}
                </p>
              ))}
              <p className="text-sm text-fg-muted">
                Kaydı yine de açabilirsiniz; sistem birleştirme yapmaz.
              </p>
            </div>
          ) : null}
        </form>
      )}
    </Modal>
  );
}
